import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {useDatabase} from '../context/DatabaseContext';
import {useToast} from '../context/ToastContext';
import {colors} from '../theme/colors';

const ShoppingListScreen = () => {
  const navigate = useNavigate();
  const {getShoppingList, addShoppingItem, toggleShoppingItem, deleteShoppingItem, clearCheckedShoppingItems} = useDatabase();
  const {success, error: showError} = useToast();
  const [items, setItems] = useState([]);
  const [newItemName, setNewItemName] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadShoppingList();
  }, []);

  const loadShoppingList = async () => {
    try {
      const list = await getShoppingList();
      // Sort: unchecked items first, then checked items
      const sorted = [...list].sort((a, b) => {
        if (a.checked === b.checked) return 0;
        return a.checked ? 1 : -1;
      });
      setItems(sorted);
    } catch (err) {
      console.error('Error loading shopping list:', err);
      showError('נכשל בטעינת רשימת הקניות');
    }
  };

  const handleAddItem = async () => {
    if (!newItemName.trim()) {
      showError('אנא הזן שם פריט');
      return;
    }

    setLoading(true);
    try {
      const added = await addShoppingItem(newItemName.trim());
      if (added) {
        success('פריט נוסף לרשימה');
        setNewItemName('');
        await loadShoppingList();
      } else {
        showError('נכשל בהוספת הפריט');
      }
    } catch (err) {
      console.error('Error adding item:', err);
      showError('נכשל בהוספת הפריט');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleItem = async (itemId) => {
    try {
      const toggled = await toggleShoppingItem(itemId);
      if (toggled) {
        await loadShoppingList();
      }
    } catch (err) {
      console.error('Error toggling item:', err);
      showError('נכשל בעדכון הפריט');
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      const deleted = await deleteShoppingItem(itemId);
      if (deleted) {
        success('פריט נמחק');
        await loadShoppingList();
      } else {
        showError('נכשל במחיקת הפריט');
      }
    } catch (err) {
      console.error('Error deleting item:', err);
      showError('נכשל במחיקת הפריט');
    }
  };

  const handleClearChecked = async () => {
    const checkedCount = items.filter(i => i.checked).length;
    if (checkedCount === 0) {
      showError('אין פריטים מסומנים למחיקה');
      return;
    }

    try {
      const cleared = await clearCheckedShoppingItems();
      if (cleared) {
        success(`${checkedCount} פריטים נמחקו`);
        await loadShoppingList();
      } else {
        showError('נכשל במחיקת הפריטים');
      }
    } catch (err) {
      console.error('Error clearing checked items:', err);
      showError('נכשל במחיקת הפריטים');
    }
  };

  const checkedCount = items.filter(i => i.checked).length;
  const uncheckedCount = items.filter(i => !i.checked).length;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button
          style={styles.backButton}
          onClick={() => navigate('/')}
          aria-label="Back">
          ←
        </button>
        <div style={styles.headerTitle}>רשימת קניות</div>
        <div style={styles.headerRight}></div>
      </div>

      {/* Stats */}
      <div style={styles.statsSection}>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{uncheckedCount}</div>
          <div style={styles.statLabel}>נותר</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{checkedCount}</div>
          <div style={styles.statLabel}>הושלם</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{items.length}</div>
          <div style={styles.statLabel}>סה"כ</div>
        </div>
      </div>

      {/* Add Item Form */}
      <div style={styles.addSection}>
        <div style={styles.inputContainer}>
          <label htmlFor="shopping-item-input" style={{display: 'none'}}>הוסף פריט חדש לרשימת הקניות</label>
          <input
            id="shopping-item-input"
            type="text"
            placeholder="הוסף פריט חדש..."
            value={newItemName}
            onChange={(e) => setNewItemName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddItem();
              }
            }}
            style={styles.input}
            dir="rtl"
            disabled={loading}
            aria-label="Add new shopping list item"
          />
          <button
            style={styles.addButton}
            onClick={handleAddItem}
            disabled={loading || !newItemName.trim()}
            className="btn-primary"
            aria-label="Add item to shopping list"
            type="button">
            הוסף
          </button>
        </div>
      </div>

      {/* Shopping List */}
      <div style={styles.listSection}>
        {items.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon} aria-hidden="true">📝</div>
            <div style={styles.emptyText}>הרשימה ריקה</div>
            <div style={styles.emptySubtext}>הוסף פריטים לרשימת הקניות</div>
          </div>
        ) : (
          <>
            {items.map(item => (
              <div
                key={item.id}
                style={{
                  ...styles.listItem,
                  ...(item.checked ? styles.listItemChecked : {}),
                }}
                className="card-hover">
                <div style={styles.itemContent}>
                  <input
                    type="checkbox"
                    checked={item.checked}
                    onChange={() => handleToggleItem(item.id)}
                    style={styles.checkbox}
                    aria-label={`Mark ${item.name} as ${item.checked ? 'unchecked' : 'checked'}`}
                  />
                  <div
                    style={{
                      ...styles.itemName,
                      ...(item.checked ? styles.itemNameChecked : {}),
                    }}>
                    {item.name}
                  </div>
                </div>
                <button
                  style={styles.deleteButton}
                  onClick={() => handleDeleteItem(item.id)}
                  aria-label={`Delete ${item.name} from shopping list`}
                  type="button">
                  <span aria-hidden="true">🗑️</span>
                </button>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Clear Checked Button */}
      {checkedCount > 0 && (
        <div style={styles.clearSection}>
          <button
            style={styles.clearButton}
            onClick={handleClearChecked}
            className="btn-secondary"
            aria-label={`Delete ${checkedCount} checked items`}
            type="button">
            מחק פריטים מסומנים ({checkedCount})
          </button>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    flex: 1,
    backgroundColor: colors.bg,
    minHeight: '100vh',
    paddingBottom: 20,
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px',
    paddingTop: '24px',
  },
  backButton: {
    background: 'none',
    border: 'none',
    color: colors.primary,
    fontSize: 24,
    cursor: 'pointer',
    padding: 8,
    width: 40,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.textPrimary,
    flex: 1,
    textAlign: 'center',
  },
  headerRight: {
    width: 40,
  },
  statsSection: {
    display: 'flex',
    flexDirection: 'row',
    gap: 12,
    padding: '0 20px',
    marginBottom: 20,
  },
  statCard: {
    flex: 1,
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: 16,
    border: `1px solid ${colors.border}`,
    textAlign: 'center',
  },
  statValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: colors.textTertiary,
  },
  addSection: {
    padding: '0 20px',
    marginBottom: 20,
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 12,
  },
  input: {
    flex: 1,
    height: 48,
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    padding: '12px 16px',
    fontSize: 16,
    color: colors.textPrimary,
    outline: 'none',
  },
  addButton: {
    height: 48,
    padding: '12px 24px',
    backgroundColor: colors.primary,
    color: colors.textOnPrimary,
    border: 'none',
    borderRadius: 12,
    fontSize: 16,
    fontWeight: '600',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  listSection: {
    padding: '0 20px',
    marginBottom: 20,
  },
  listItem: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    border: `1px solid ${colors.border}`,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  listItemChecked: {
    opacity: 0.6,
    backgroundColor: colors.bg,
  },
  itemContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  checkbox: {
    width: 20,
    height: 20,
    cursor: 'pointer',
    accentColor: colors.primary,
  },
  itemName: {
    fontSize: 16,
    color: colors.textPrimary,
    flex: 1,
    textAlign: 'right',
  },
  itemNameChecked: {
    textDecoration: 'line-through',
    color: colors.textSecondary,
  },
  deleteButton: {
    background: 'none',
    border: 'none',
    fontSize: 18,
    cursor: 'pointer',
    padding: 8,
    opacity: 0.7,
    transition: 'opacity 200ms ease',
  },
  clearSection: {
    padding: '0 20px',
  },
  clearButton: {
    width: '100%',
    height: 48,
    backgroundColor: colors.surface,
    color: colors.error,
    border: `1px solid ${colors.error}`,
    borderRadius: 12,
    fontSize: 16,
    fontWeight: '600',
    cursor: 'pointer',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
  },
  emptyIcon: {
    fontSize: 64,
    marginBottom: 16,
  },
  emptyText: {
    fontSize: 20,
    fontWeight: '600',
    color: colors.textPrimary,
    marginBottom: 8,
  },
  emptySubtext: {
    fontSize: 14,
    color: colors.textSecondary,
  },
};

export default ShoppingListScreen;

